import {Form} from "../../helpers/forms/Form";
import {GlobalState} from "./State/GlobalState";
import {getSingleInputByNameV3} from "../../helpers/forms/formInputsHelpers";

// noinspection JSUnusedGlobalSymbols -- Imported via d.ts
class PublicTicketsForm extends Form {
    /** @var {GlobalState} */
    state;

    constructor(config) {
        super({
            fields: [],
            ...config,
        });

        // Initialize global state
        this.state = new GlobalState();

        this.state.setPublicTicketConfig(this.getPublicTicketConfig());

        const entryContext = this.getInitialEntryContext();

        this.state.baseRoute = entryContext.baseRoute;
        this.state.currentStage = entryContext.startStage;
        this.state.setHiddenStages(entryContext.hiddenStages);

        this.state.setIsCurrentEventView(this.shouldUseCurrentEventMode(entryContext));

        this.loadTrackingContextFromServer(entryContext);

        if (entryContext.preselectedEvent) {
            this.state.setParentEvent(entryContext.preselectedEvent)
        }

        const preselectedEventDates = entryContext.preselectedEventDates || [];

        if (preselectedEventDates.length) {
            this.state.setSelectedDates(preselectedEventDates)
        } else if (entryContext.preselectedEventDate) {
            this.state.setEventDate(entryContext.preselectedEventDate)
        }

        if (entryContext.preselectedSeatType) {
            this.state.setSeatType(entryContext.preselectedSeatType)
        }

        // Initial render
        this.state.stageManager.renderStage();
    }

    getPublicTicketConfig() {
        const configFromServer = this.parseHiddenInputData('public_ticket_config');

        if (!configFromServer || typeof configFromServer !== 'object') {
            return {};
        }

        return {
            multimapTutorialShow: configFromServer.multimapTutorialShow === true,
            rvStallUpsell: this.normalizeRvStallUpsellConfig(configFromServer.rvStallUpsell),
            forcedMinimumAddon: this.normalizeForcedMinimumAddonConfig(configFromServer.forcedMinimumAddon),
        };
    }

    normalizeForcedMinimumAddonConfig(config) {
        if (!config || typeof config !== 'object') {
            return {
                key: '',
            };
        }

        return {
            key: this.normalizePublicIdConfigValue(config.key),
        };
    }

    normalizeRvStallUpsellConfig(config) {
        const disabledConfig = {
            enabled: false,
            parentMultiMapPublicId: '',
            rvChildMapPublicId: '',
            stallsChildMapPublicId: '',
        };

        if (!config || typeof config !== 'object') {
            return disabledConfig;
        }

        const parentMultiMapPublicId = this.normalizePublicIdConfigValue(config.parentMultiMapPublicId);
        const rvChildMapPublicId = this.normalizePublicIdConfigValue(config.rvChildMapPublicId);
        const stallsChildMapPublicId = this.normalizePublicIdConfigValue(config.stallsChildMapPublicId);

        if (config.enabled !== true || !parentMultiMapPublicId || !rvChildMapPublicId || !stallsChildMapPublicId) {
            return disabledConfig;
        }

        return {
            enabled: true,
            parentMultiMapPublicId,
            rvChildMapPublicId,
            stallsChildMapPublicId,
        };
    }

    normalizePublicIdConfigValue(value) {
        return typeof value === 'string'
            ? value.trim()
            : '';
    }

    getInitialEntryContext() {
        const contextFromServer = this.parseHiddenInputData('tickets_entry_context');
        const normalizedServerContext = this.normalizeEntryContext(contextFromServer);

        if (normalizedServerContext) {
            return normalizedServerContext;
        }

        return this.getLegacyEntryContext();
    }

    getLegacyEntryContext() {
        const preselectedEventDate = this.parseHiddenInputData('preselected_event_date');
        const preselectedEvent = this.parseHiddenInputData('preselected_event');
        const baseRoute = getSingleInputByNameV3(document.body, 'base_route') || '';

        if (preselectedEvent && preselectedEventDate) {
            return {
                startStage: 'seat-select',
                hiddenStages: ['show-select', 'date-select'],
                preselectedEvent,
                preselectedEventDate,
                preselectedEventDates: [],
                preselectedSeatType: 'reserved',
                baseRoute,
            };
        }

        return {
            startStage: 'show-select',
            hiddenStages: [],
            preselectedEvent: null,
            preselectedEventDate: null,
            preselectedEventDates: [],
            preselectedSeatType: null,
            baseRoute,
        };
    }

    normalizeEntryContext(context) {
        if (!context || typeof context !== 'object') {
            return null;
        }

        const defaultBaseRoute = getSingleInputByNameV3(document.body, 'base_route') || '';

        /** @type {PublicTicketsFormDefs.Stage[]} */
        const availableStages = ['show-select', 'date-select', 'seat-select', 'seat-type-select', 'payment-info', 'summary'];

        const startStage = availableStages.includes(context.startStage)
            ? context.startStage
            : 'show-select';

        const hiddenStages = Array.isArray(context.hiddenStages)
            ? context.hiddenStages.filter(stage => availableStages.includes(stage))
            : [];

        const preselectedSeatType = ['reserved', 'general-admission'].includes(context.preselectedSeatType)
            ? context.preselectedSeatType
            : null;

        const adTrackingKeyword = (typeof context.adTrackingKeyword === 'string')
            ? context.adTrackingKeyword
            : '';

        const adTrackingSessionPublicId = (typeof context.adTrackingSessionPublicId === 'string')
            ? context.adTrackingSessionPublicId
            : '';

        return {
            startStage,
            hiddenStages,
            preselectedEvent: context.preselectedEvent || null,
            preselectedEventDate: context.preselectedEventDate || null,
            preselectedEventDates: Array.isArray(context.preselectedEventDates) ? context.preselectedEventDates : [],
            preselectedSeatType,
            baseRoute: context.baseRoute || defaultBaseRoute,
            adTrackingKeyword,
            adTrackingSessionPublicId,
        };
    }

    loadTrackingContextFromServer(entryContext = null) {
        try {
            const trackingKeywordFromContext = entryContext && typeof entryContext.adTrackingKeyword === 'string'
                ? entryContext.adTrackingKeyword
                : '';

            const trackingSessionIdFromContext = entryContext && typeof entryContext.adTrackingSessionPublicId === 'string'
                ? entryContext.adTrackingSessionPublicId
                : '';

            const trackingKeyword = trackingKeywordFromContext || getSingleInputByNameV3(document.body, 'ad_tracking_keyword') || '';
            const trackingSessionPublicId = trackingSessionIdFromContext || getSingleInputByNameV3(document.body, 'ad_tracking_session_public_id') || '';

            this.state.setAdTrackingContext(trackingKeyword, trackingSessionPublicId);
        } catch (error) {
            // Tracking bootstrap is best-effort and should never block the purchase flow.
        }
    }

    shouldUseCurrentEventMode(entryContext) {
        return (
            entryContext.startStage === 'seat-select' &&
            entryContext.hiddenStages.includes('show-select') &&
            entryContext.hiddenStages.includes('date-select')
        );
    }

    /**
     * Safely checks for json encoded strings input values
     * @param inputName
     * @return {any|null}
     */
    parseHiddenInputData(inputName) {
        try {
            const inputValue = getSingleInputByNameV3(document.body, inputName);
            return inputValue ? JSON.parse(inputValue) : null;
        } catch (error) {
            return null;
        }
    }

}

export {PublicTicketsForm}
